import React from "react";
import { useTranslation } from "../../hooks/useTranslation";
import PageShell, { PageHero } from "../../v2/pages/PageShell";

const PrivacyPolicy: React.FC = () => {
  const { currentLanguage } = useTranslation();
  const fr = (
    <>
      <PageShell
        seo={{
          title: "Politique de confidentialité | Lave Tes Baskets",
          description: "Quelles données nous collectons, pourquoi, combien de temps, et vos droits.",
          canonicalPath: `/${currentLanguage}/privacy`,
        }}
      >
        <PageHero
          crumbs={[{ label: "Accueil", to: `/${currentLanguage}` }, { label: "Politique de confidentialité" }]}
          index="§"
          eyebrow={"Légal"}
          title={"Politique de confidentialité"}
        />
        <section className="bg-bone py-14 text-ink sm:py-20">
          <div className="v2-prose mx-auto max-w-3xl px-4 sm:px-6">
              <p className="mb-4">Dernière mise à jour : 25/08/2025</p>

              <p className="mb-4">
                Cette politique explique comment nous traitons vos données
                personnelles conformément au RGPD (Règlement Général sur la
                Protection des Données) et à la loi française (Loi Informatique
                et Libertés).
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                1. Responsable du traitement
              </h3>
              <p className="mb-4">
                Lave Tes Baskets (LTB) est responsable du traitement des données
                collectées via le site et ses services.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                2. Données collectées
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  Données de compte: nom, prénom, email, mot de passe (haché)
                </li>
                <li>Données de contact: téléphone (facultatif)</li>
                <li>Données de commande: adresses, détails des prestations</li>
                <li>
                  Données techniques: logs, cookies strictement nécessaires
                </li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                3. Finalités et bases légales
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  Fourniture du service (base légale: exécution du contrat)
                </li>
                <li>
                  Support client et sécurité (base légale: intérêt légitime)
                </li>
                <li>
                  Obligations légales (comptabilité, facturation, sécurité)
                </li>
                <li>Marketing et newsletters (base légale: consentement)</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                4. Destinataires et sous-traitants
              </h3>
              <p className="mb-4">
                Prestataires strictement nécessaires (hébergement, email,
                paiement, analytics le cas échéant). Les détails et pays
                d'hébergement seront précisés dans la version à jour. Les
                transferts internationaux éventuels sont encadrés par les
                Clauses Contractuelles Types.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                5. Durées de conservation
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Compte: pendant la relation + 3 ans</li>
                <li>Commandes/facturation: durée légale comptable</li>
                <li>Logs techniques: jusqu'à 12 mois</li>
                <li>
                  Consentements marketing: tant que l'abonnement est actif +
                  période limitée
                </li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                6. Exercer vos droits
              </h3>
              <p className="mb-4">
                Vous pouvez exercer vos droits (accès, rectification,
                effacement, limitation, opposition, portabilité) en nous
                contactant à privacy@lavetesbaskets.com. Nous pouvons vérifier
                votre identité. Délai de réponse: 1 mois (prolongeable dans
                certains cas). Réclamation possible auprès de la CNIL
                (www.cnil.fr).
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                7. Mineurs et profilage
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>
                  Service non destiné aux moins de 16 ans; suppression sur
                  notification.
                </li>
                <li>
                  Pas de décisions automatisées; segmentation marketing minimale
                  et désinscription possible.
                </li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                8. Réseaux sociaux et liens externes
              </h3>
              <p className="mb-4">
                Les liens vers Instagram, Facebook, Snapchat, X mènent à des
                plateformes tierces qui traitent vos données selon leurs propres
                politiques.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">9. Sécurité</h3>
              <p className="mb-4">
                Mesures techniques et organisationnelles (chiffrement des mots
                de passe, contrôle d'accès, journalisation).
              </p>

              <h3 id="cookies" className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                10. Cookies
              </h3>
              <p className="mb-4">
                Uniquement ceux nécessaires par défaut. Un bandeau de
                consentement pourra proposer d'activer des cookies
                analytiques/marketing. Vous pourrez modifier vos préférences à
                tout moment.
              </p>

              <p className="mt-8 text-sm text-gray-600">
                Pour toute question, contactez-nous: privacy@lavetesbaskets.com
              </p>
            </div>
        </section>
      </PageShell>
    </>
  );

  const en = (
    <>
      <PageShell
        seo={{
          title: "Privacy policy | Lave Tes Baskets",
          description: "What data we collect, why, for how long, and your rights.",
          canonicalPath: `/${currentLanguage}/privacy`,
        }}
      >
        <PageHero
          crumbs={[{ label: "Home", to: `/${currentLanguage}` }, { label: "Privacy policy" }]}
          index="§"
          eyebrow={"Legal"}
          title={"Privacy policy"}
        />
        <section className="bg-bone py-14 text-ink sm:py-20">
          <div className="v2-prose mx-auto max-w-3xl px-4 sm:px-6">
              <p className="mb-4">Last updated: 2025-08-25</p>

              <p className="mb-4">
                This policy explains how we process your personal data in
                accordance with the EU General Data Protection Regulation (GDPR)
                and French law, in particular the French Data Protection Act
                (Loi Informatique et Libertés). The French supervisory authority
                is the CNIL (www.cnil.fr).
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                1. Controller
              </h3>
              <p className="mb-4">
                Lave Tes Baskets (LTB) acts as the data controller for data
                collected through the website and related services.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                2. Data we collect
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Account data: name, surname, email, password (hashed)</li>
                <li>Contact data: phone (optional)</li>
                <li>Order data: addresses, service details</li>
                <li>Technical data: logs, strictly necessary cookies</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                3. Purposes and legal bases
              </h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Service provision (legal basis: contract)</li>
                <li>
                  Customer support and security (legal basis: legitimate
                  interests)
                </li>
                <li>Legal obligations (accounting, invoicing, security)</li>
                <li>Marketing with consent where required</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                4. Recipients and processors
              </h3>
              <p className="mb-4">
                Essential providers only (hosting, email), bound by
                GDPR-compliant data processing agreements.
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">5. Retention</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Account: duration of relationship + 3 years</li>
                <li>Orders/invoicing: statutory accounting period</li>
                <li>Technical logs: up to 12 months</li>
                <li>Marketing consents: while active + limited period</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                6. Your rights
              </h3>
              <p className="mb-2">
                Under GDPR: access, rectification, erasure, restriction,
                objection, portability.
              </p>
              <p className="mb-4">
                Contact: privacy@lavetesbaskets.com. Supervisory authority: CNIL
                (www.cnil.fr).
              </p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">7. Children and profiling</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Service not directed to children under 16; removal upon notice.</li>
                <li>No automated decision-making; minimal marketing segmentation with opt-out.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">8. Social media and external links</h3>
              <p className="mb-4">Links to Instagram, Facebook, Snapchat, and X lead to third‑party platforms that process your data under their own privacy policies.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">9. Security</h3>
              <p className="mb-4">
                Technical and organizational measures implemented (password
                hashing, access control, logging).
              </p>

              <h3 id="cookies" className="text-xl sm:text-2xl font-semibold mt-8 mb-3">
                10. Cookies
              </h3>
              <p className="mb-4">
                Only strictly necessary cookies are used by default. A consent
                banner may allow enabling analytics/marketing cookies. You can
                change your preferences at any time.
              </p>
            </div>
        </section>
      </PageShell>
    </>
  );

  return currentLanguage === "fr" ? fr : en;
};

export default PrivacyPolicy;
